import { useState } from 'react';

import { HowToRegOutlined } from '@mui/icons-material';
import { Box, Button, Paper, SvgIconProps, SxProps, Theme, Typography } from '@mui/material';

import { useLanguage } from '../../hooks';
import {
  CheckmarkCircle2Outline,
  ChevronLeftOutline,
  ChevronRightOutline,
  CrownSimple,
  Edit2Outline,
  Shuffle2Outline,
} from '../../icons';
import { MonitorOutlineIcon } from '../icons/monitorOutlineIcon';
import { openIntercom } from '../../utils/intercom';
import { openFrontendRouteInNewTab, paths } from '../../utils/navigation';
import { useUserDataStore } from '../../newStore/userDataStore';
import { PricingCTAButtons } from '../uiComponents/Pricing/PricingCTAButtons';
import { PRICING_CHIP } from '../uiComponents/Pricing/pricingPlanChipColors';

// Localized landing assets hosted on S3, same pattern as PlanningsLanding:
// `{name}-{es|en|pt}.{ext}`. Copy is translated via i18n; the illustration and
// slide screenshots carry baked-in UI text, so each language has its own file.
// Unsupported languages fall back to Spanish.
const S3_BASE = 'https://data-scope.s3.us-west-2.amazonaws.com/assets/signature';
const SUPPORTED_LANGS = ['es', 'pt', 'en'];

export const assetsForLang = (lang: string) => {
  const l = SUPPORTED_LANGS.includes(lang) ? lang : 'es';
  return {
    illustration: `${S3_BASE}/ilustracion-firma-${l}.svg`,
    slides: [1, 2, 3, 4].map((n) => `${S3_BASE}/slide-${n}-${l}.png`),
  };
};

// Plan-gated landing for electronic signature (Free/Starter, tier :none),
// ported from the prototype (prototype/pricing-firma-electronica) to match the
// paid-module empty-state standard shared with Tareas asignadas / Plannings:
// crown tag, outcome title, colored-icon bullets, a Dirección del Trabajo legal
// banner, illustration, and a demo carousel walking the full signing flow.
//
// The shared DemoCarousel* primitives are fixed at two steps; this flow has
// four, so the step indicator and nav are kept inline here rather than
// widening the shared components for a single caller.

// Logo Dirección del Trabajo: same asset as the signed-document validation
// footer (page shown after scanning the QR).
const DT_LOGO = 'https://data-scope.s3.us-west-2.amazonaws.com/assets/signature-DT-icon.png';

const customStyles: Record<string, SxProps<Theme>> = {
  // Outer page padding: the prototype renders the landing inside a
  // `padding: 24px 30px` wrapper, and the component owns that padding in
  // production. No max width -- like the other module landings (Plannings,
  // Tareas), the header uses space-between so the illustration hugs the right
  // edge and fills the content width instead of leaving a right gutter.
  root: { padding: '24px 30px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '60px',
    marginBottom: '45px',
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '600px',
  },
  tag: {
    alignSelf: 'flex-start',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: PRICING_CHIP.text,
    backgroundColor: PRICING_CHIP.tintedBg,
    border: `1px solid ${PRICING_CHIP.border}`,
    borderRadius: '4px',
    padding: '4px 10px',
    marginBottom: '12px',
  },
  tagIcon: { fontSize: '16px', color: PRICING_CHIP.text },
  title: {
    fontSize: { xs: '24px', md: '30px' },
    fontWeight: 600,
    lineHeight: { xs: '32px', md: '40px' },
    color: '#4760F6',
    marginBottom: '20px',
  },
  intro: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    color: '#3B3C40',
    marginBottom: '24px',
  },
  features: { display: 'flex', flexDirection: 'column', gap: '20px', margin: 0, padding: 0 },
  feature: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    color: '#3B3C40',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    listStyle: 'none',
  },
  featureIconBox: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  featureIcon: { width: '24px', height: '24px', fontSize: '24px' },
  dtBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    borderRadius: '8px',
    padding: '8px 16px',
    border: '1px solid #4760F6',
    backgroundColor: '#D8EFFE',
    marginTop: '24px',
  },
  dtLogo: { width: '48px', height: '48px', objectFit: 'contain', flexShrink: 0 },
  dtText: { fontSize: '14px', fontWeight: 400, lineHeight: '22px', color: '#333333' },
  ctaContainer: { marginTop: '32px' },
  illustration: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '@media (max-width: 768px)': { display: 'none' },
  },
  illustrationSvg: { width: '100%', height: 'auto', maxWidth: '640px' },
  paper: {
    padding: { xs: '32px 24px', md: '45px' },
    borderRadius: '20px',
    boxShadow: '0px 2.1px 8.39px 0px rgba(0, 0, 0, 0.09)',
    marginBottom: '45px',
  },
  demoBlockContainer: {
    display: 'flex',
    gap: '40px',
    alignItems: 'center',
    '@media (max-width: 900px)': { flexDirection: 'column' },
  },
  demoImage: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '100%',
  },
  // Shadow + 8px radius applied to the image itself, no wrapping box (design
  // review, Vale). The last slide (dynamic signer) is a floating card on a
  // transparent background, so it gets neither radius nor shadow -- see the
  // per-slide override on the <img>.
  demoImg: {
    width: '464px',
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '8px',
    boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.09)',
  },
  demoImgLastSlide: { borderRadius: 0, boxShadow: 'none' },
  demoContent: { flex: 1.2 },
  demoStepsContainer: { display: 'flex', gap: '10px', marginBottom: '40px' },
  demoStep: {
    width: '79px',
    height: '8px',
    borderRadius: '8px',
    cursor: 'pointer',
    background: '#DDE0E5',
  },
  demoStepActive: { background: 'linear-gradient(90deg, #3E8FFA 43.56%, #4760F6 100%)' },
  demoTitle: {
    fontSize: '24px',
    fontWeight: 600,
    lineHeight: '32px',
    color: '#4760F6',
    marginBottom: '20px',
  },
  demoCardTitleRow: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  enterpriseChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    fontWeight: 500,
    color: PRICING_CHIP.text,
    backgroundColor: PRICING_CHIP.tintedBg,
    border: `1px solid ${PRICING_CHIP.border}`,
    borderRadius: '4px',
    padding: '2px 8px',
  },
  enterpriseChipIcon: { fontSize: '16px', color: PRICING_CHIP.text },
  demoCard: {
    display: 'flex',
    gap: '15px',
    padding: '28px',
    borderRadius: '20px',
    backgroundColor: '#F0F5FF',
    flexDirection: 'column',
    width: '100%',
  },
  demoIcon: {
    width: '32px',
    height: '32px',
    color: (theme: Theme) => theme.palette.primary.main,
    '& path': { fill: (theme: Theme) => theme.palette.primary.main },
  },
  demoCardTitle: { fontSize: '20px', fontWeight: 500, lineHeight: '28px', color: '#3B3C40' },
  demoCardDescription: { fontSize: '16px', fontWeight: 400, lineHeight: '24px', color: '#3B3C40' },
  demoButtonsContainer: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'space-between',
    marginTop: '16px',
  },
  navButtonHidden: { opacity: 0, pointerEvents: 'none' },
};

type IconComponent = (props: SvgIconProps) => JSX.Element;

interface Feature {
  text: string;
  Icon: IconComponent;
  background: string;
  color: string;
}

export function FirmaPricingEmptyState() {
  const lang = useLanguage();
  const fsbLang = lang.formSignatureBuilder;
  const langCode = useUserDataStore((state) => state.lang) || 'es';
  const assets = assetsForLang(langCode);
  const esAssets = assetsForLang('es');

  // If a localized S3 asset is missing, fall back to the Spanish one; if that
  // also fails, hide the broken image instead of showing the browser's icon.
  const handleAssetError =
    (fallbackSrc: string) => (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      if (img.src !== fallbackSrc) {
        img.src = fallbackSrc;
      } else {
        img.style.display = 'none';
      }
    };

  const [demo, setDemo] = useState(0);

  const handleKnowPlans = () => {
    openFrontendRouteInNewTab(paths.billing.index);
  };

  const handleContactSales = () => {
    openIntercom(fsbLang.pricingEmptyStateContactSalesMessage);
  };

  const handleStepKeyDown = (index: number) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setDemo(index);
    }
  };

  // Value bullets: 1) request signatures, 2) approval flow/branching,
  // 3) traceability/compliance. Colors match the prototype.
  const features: Feature[] = [
    {
      text: fsbLang.pricingEmptyStateFeature1,
      Icon: HowToRegOutlined as IconComponent,
      background: '#F0F5FF',
      color: '#3E8FFA',
    },
    {
      text: fsbLang.pricingEmptyStateFeature2,
      Icon: Shuffle2Outline,
      background: '#FFF2E8',
      color: '#FA541C',
    },
    {
      text: fsbLang.pricingEmptyStateFeature3,
      Icon: CheckmarkCircle2Outline,
      background: '#E5FCD7',
      color: '#52C41A',
    },
  ];

  const demos = [
    {
      key: 'firmantes',
      image: assets.slides[0],
      Icon: HowToRegOutlined as IconComponent,
      title: fsbLang.pricingEmptyStateSlide1Title,
      description: fsbLang.pricingEmptyStateSlide1Description,
    },
    {
      key: 'seguimiento',
      image: assets.slides[1],
      Icon: CheckmarkCircle2Outline,
      title: fsbLang.pricingEmptyStateSlide2Title,
      description: fsbLang.pricingEmptyStateSlide2Description,
    },
    {
      key: 'historial',
      image: assets.slides[2],
      Icon: MonitorOutlineIcon as IconComponent,
      title: fsbLang.pricingEmptyStateSlide3Title,
      description: fsbLang.pricingEmptyStateSlide3Description,
    },
    {
      key: 'dinamica',
      image: assets.slides[3],
      Icon: Edit2Outline,
      title: fsbLang.pricingEmptyStateSlide4Title,
      description: fsbLang.pricingEmptyStateSlide4Description,
      enterprise: true,
    },
  ];

  const current = demos[demo];
  const CurrentIcon = current.Icon;

  return (
    <Box sx={customStyles.root} data-testid='firma-pricing-empty-state'>
      <Box sx={customStyles.header}>
        <Box sx={customStyles.headerContent}>
          <Typography sx={customStyles.tag}>
            <CrownSimple sx={customStyles.tagIcon} />
            {fsbLang.pricingEmptyStateTag}
          </Typography>
          <Typography sx={customStyles.title}>{fsbLang.pricingEmptyStateTitle}</Typography>
          <Typography sx={customStyles.intro}>{fsbLang.pricingEmptyStateIntro}</Typography>
          <Box sx={customStyles.features} component='ul'>
            {features.map(({ text, Icon, background, color }) => (
              <Typography key={text} component='li' sx={customStyles.feature}>
                <Box sx={{ ...customStyles.featureIconBox, backgroundColor: background }}>
                  <Icon sx={{ ...customStyles.featureIcon, color, '& path': { fill: color } }} />
                </Box>
                {text}
              </Typography>
            ))}
          </Box>
          {/* Dirección del Trabajo is a Chile-specific validation, shown only
              in Spanish (product decision). */}
          {langCode === 'es' && (
            <Box sx={customStyles.dtBanner}>
              <Box
                component='img'
                src={DT_LOGO}
                alt='Dirección del Trabajo'
                sx={customStyles.dtLogo}
              />
              <Typography sx={customStyles.dtText}>{fsbLang.pricingEmptyStateDtBanner}</Typography>
            </Box>
          )}
          <Box sx={customStyles.ctaContainer}>
            <PricingCTAButtons
              knowPlansLabel={fsbLang.pricingEmptyStateKnowPlans}
              contactSalesLabel={fsbLang.pricingEmptyStateContactSales}
              onKnowPlans={handleKnowPlans}
              onContactSales={handleContactSales}
            />
          </Box>
        </Box>
        <Box sx={customStyles.illustration}>
          <Box
            component='img'
            src={assets.illustration}
            alt='Ilustración de Firma electrónica'
            onError={handleAssetError(esAssets.illustration)}
            sx={customStyles.illustrationSvg}
          />
        </Box>
      </Box>

      <Paper sx={customStyles.paper}>
        <Box sx={customStyles.demoBlockContainer}>
          <Box sx={customStyles.demoImage}>
            <Box
              component='img'
              src={current.image}
              alt={current.title}
              onError={handleAssetError(esAssets.slides[demo])}
              sx={
                [
                  customStyles.demoImg,
                  demo === demos.length - 1 && customStyles.demoImgLastSlide,
                ] as SxProps<Theme>
              }
            />
          </Box>
          <Box sx={customStyles.demoContent}>
            <Box sx={customStyles.demoStepsContainer}>
              {demos.map((d, i) => (
                <Box
                  key={d.key}
                  component='span'
                  role='button'
                  tabIndex={0}
                  aria-label={d.title}
                  aria-current={i === demo}
                  onClick={() => setDemo(i)}
                  onKeyDown={handleStepKeyDown(i)}
                  data-testid={`firma-pricing-demo-step-${i}`}
                  sx={
                    [customStyles.demoStep, i === demo && customStyles.demoStepActive] as SxProps<Theme>
                  }
                />
              ))}
            </Box>
            <Typography sx={customStyles.demoTitle}>{fsbLang.pricingEmptyStateDemoTitle}</Typography>
            <Box sx={customStyles.demoCard}>
              <CurrentIcon sx={customStyles.demoIcon} />
              <Box sx={customStyles.demoCardTitleRow}>
                <Typography sx={customStyles.demoCardTitle}>{current.title}</Typography>
                {current.enterprise && (
                  <Box component='span' sx={customStyles.enterpriseChip}>
                    <CrownSimple sx={customStyles.enterpriseChipIcon} />
                    {fsbLang.pricingEmptyStateEnterpriseChip}
                  </Box>
                )}
              </Box>
              <Typography sx={customStyles.demoCardDescription}>{current.description}</Typography>
            </Box>
            <Box sx={customStyles.demoButtonsContainer}>
              <Button
                color='primary'
                startIcon={<ChevronLeftOutline />}
                disabled={demo === 0}
                onClick={() => setDemo((d) => Math.max(0, d - 1))}
                sx={demo === 0 ? customStyles.navButtonHidden : undefined}
                data-testid='firma-pricing-demo-prev'
              >
                {fsbLang.pricingEmptyStatePrev}
              </Button>
              <Button
                color='primary'
                endIcon={<ChevronRightOutline />}
                disabled={demo === demos.length - 1}
                onClick={() => setDemo((d) => Math.min(demos.length - 1, d + 1))}
                sx={demo === demos.length - 1 ? customStyles.navButtonHidden : undefined}
                data-testid='firma-pricing-demo-next'
              >
                {fsbLang.pricingEmptyStateNext}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
